import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "./index.css";
import Home from "./Pages/Home";
import InStock from "./Pages/InStock";
import ComingSoon from "./Pages/ComingSoon";
import NavBar from "./Components/NavBar";
import LoginSignup from "./Pages/LoginSignup";
import VerifyEmail from "./Pages/VerifyEmail";
import Dashboard from "./Pages/Dashboard";
import ReactGA from "react-ga4";
import EggListSignup from "./Pages/EggList";
import { Box, Typography } from "@mui/material";
import Logout from "./Pages/Logout";
import ResetPassword from "./Pages/ResetPassword";
import TheFarmstand from "./Pages/TheFarmstand";
import PayDeposit from "./Pages/PayDeposit";
import PayDepositSuccess from "./Pages/PayDepositSuccess";
import Store from "./Pages/Store";
import StoreItemDetail from "./Pages/Store/StoreItemDetail";
import Preorders from "./Pages/Store/Preorders";
import InStockStore from "./Pages/Store/InStock";
import Cart from "./Pages/Store/Cart";
import Orders from "./Pages/Store/Orders";
import CheckoutSuccess from "./Pages/PayDepositSuccess/CheckoutSuccess";
import FloatingCartIcon from "./Components/FloatingCartIcon";
import EggsInInverGroveHeights from "./SEOPages/EggsInInverGrove";
import EggCartonShuffle from "./Components/EggCartonShuffle/EggCartonShuffle";
import AboutOurFlock from "./Pages/AboutOurFlock";
import Employment from "./Pages/Employment";
import AdminApplications from "./Pages/AdminApplications";
import InvoicePage from "./Pages/Invoice";
import PlantDetail from "./Pages/PlantDetail";
import POS from "./Pages/POS";
import supabase from "./supabaseClient";

const trackingId = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
ReactGA.initialize(trackingId);

const BANNER_COLORS = {
	light_green: { backgroundColor: "#a1f577", textColor: "#111111" },
	light_yellow: { backgroundColor: "#fff9c4", textColor: "#111111" },
	light_red: { backgroundColor: "#ffcccb", textColor: "#111111" },
	light_blue: { backgroundColor: "#d7ecff", textColor: "#111111" },
	light_purple: { backgroundColor: "#eadcff", textColor: "#111111" },
};

const App = () => {
	const [bannerLoading, setBannerLoading] = useState(true);
	const [banner, setBanner] = useState(null);

	useEffect(() => {
		const fetchBanner = async () => {
			try {
				const { data } = await supabase
					.from("site_banner_settings")
					.select("message, color_option, is_enabled")
					.eq("singleton", true)
					.maybeSingle();
				setBanner(data || null);
			} catch (error) {
				setBanner(null);
			} finally {
				setBannerLoading(false);
			}
		};

		fetchBanner();
	}, []);

	const message = banner?.message?.trim();
	const shouldShowBanner = !bannerLoading && banner?.is_enabled && !!message;
	const selectedColor =
		BANNER_COLORS[banner?.color_option] || BANNER_COLORS.light_yellow;

	return (
		<>
			{shouldShowBanner && (
				<Box padding={2} backgroundColor={selectedColor.backgroundColor}>
					<Typography
						variant="h4"
						align="center"
						sx={{ color: selectedColor.textColor }}
					>
						{message}
					</Typography>
				</Box>
			)}

			<NavBar />
			<FloatingCartIcon />
			{/*
			add this in when want to show the popover
			<OpeningHoursPopover />
			*/}
			<BrowserRouter>
				<Routes>
					<Route path="/" element={<Home />} />
					<Route path="/in-stock" element={<InStock />} />
					<Route path="/greenhouse" element={<InStock />} />
					<Route path="/plants" element={<InStock />} />
					<Route path="/coming-soon" element={<ComingSoon />} />
					<Route path="/pay-deposit" element={<PayDeposit />} />
					<Route
						path="/login"
						element={
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									//padding 15 if larger than 600px
									padding: { xs: 2, sm: 4, md: 10, lg: 20 },
								}}
							>
								<LoginSignup doRedirect={true} />
							</Box>
						}
					/>
					<Route
						path="/signup"
						element={
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									//padding 15 if larger than 600px
									padding: { xs: 2, sm: 4, md: 10, lg: 20 },
								}}
							>
								<LoginSignup doRedirect={true} />
							</Box>
						}
					/>
					<Route path="/logout" element={<Logout />} />
					<Route path="/verify" element={<VerifyEmail />} />
					<Route path="/dashboard" element={<Dashboard />} />
					<Route path="/egg-list" element={<EggListSignup />} />
					<Route path="/egg-request-list" element={<EggListSignup />} />
					<Route path="/reset-password" element={<ResetPassword />} />
					<Route path="/farmstand" element={<TheFarmstand />} />
					<Route path="/deposit-success" element={<PayDepositSuccess />} />
					<Route path="/store" element={<Store />} />
					<Route path="/store/:storeItemId" element={<StoreItemDetail />} />
					<Route path="/store/preorders" element={<Preorders />} />
					<Route path="/store/in-stock" element={<InStockStore />} />
					<Route path="/cart" element={<Cart />} />
					<Route path="/orders" element={<Orders />} />
					<Route
						path="/pay-deposit-success/checkout-success"
						element={<CheckoutSuccess />}
					/>
					<Route
						path="/eggs-inver-grove-heights"
						element={<EggsInInverGroveHeights />}
					/>
					<Route path="/hatching-game" element={<EggCartonShuffle />} />
					<Route path="/egg-game" element={<EggCartonShuffle />} />
					<Route path="/about-our-flock" element={<AboutOurFlock />} />
					<Route path="/employment" element={<Employment />} />
					<Route path="/admin/applications" element={<AdminApplications />} />
					<Route path="/invoice/:invoiceId" element={<InvoicePage />} />
					<Route path="/plants/:plantSlug" element={<PlantDetail />} />
					<Route path="/pos" element={<POS />} />
				</Routes>
			</BrowserRouter>
		</>
	);
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
	<React.StrictMode>
		<App />
	</React.StrictMode>,
);
